import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { PermissionService } from '@app/core/services/permission.service';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';
import {
  ReturnExceptionFeatureService,
  ReturnExceptionListItem,
} from './services/return-exception-feature.service';
import {
  RETURN_EXCEPTION_ACTION_COLUMN,
  RETURN_EXCEPTION_DEFAULT_COLUMNS_ORDER,
  RETURN_EXCEPTION_DEFAULT_CONFIG,
  RETURN_EXCEPTION_FLEXIBLE_COLUMNS_ORDER,
  RETURN_EXCEPTION_SEARCH_CONFIG,
  RETURN_EXCEPTION_STATUS,
  RETURN_EXCEPTION_TABLE_COLUMNS,
} from './return-exception.config';
import { ReturnExceptionDetailDialogComponent } from './components/return-exception-detail-dialog/return-exception-detail-dialog.component';
import { ReturnExceptionCancelDialogComponent } from './components/return-exception-cancel-dialog/return-exception-cancel-dialog.component';

@Component({
  selector: 'app-return-exception',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    AppButtonComponent,
    ToastModule,
    TagModule,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService, DialogService],
  templateUrl: './return-exception.component.html',
  styleUrl: './return-exception.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReturnExceptionComponent implements OnInit, OnDestroy {
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly featureService = inject(ReturnExceptionFeatureService);
  private readonly permissionService = inject(PermissionService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);

  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  canView = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('after.return_exception.view');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('after.return_exception.approve');
  });
  canCancel = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('after.return_exception.cancel');
  });
  hasRowActions = computed(
    () => this.canView() || this.canApprove() || this.canCancel(),
  );

  searchFormConfig: FormFieldConfig[] = RETURN_EXCEPTION_SEARCH_CONFIG;
  searchParams = signal<SearchFormData>({});
  items = signal<ReturnExceptionListItem[]>([]);
  selectedItems = signal<ReturnExceptionListItem[]>([]);
  loading = signal(false);
  actionLoading = signal(false);
  totalRecords = signal(0);
  pageSize = signal<number>(RETURN_EXCEPTION_DEFAULT_CONFIG.PAGE_SIZE);
  first = signal(0);

  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  private confirmationHandler: (() => void) | null = null;

  private dialogRef: DynamicDialogRef | null = null;

  pendingSelectedCount = computed(
    () =>
      this.selectedItems().filter((row) => this.isPending(row)).length,
  );

  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      RETURN_EXCEPTION_TABLE_COLUMNS.find((c) => c.prop === prop);
    const cols: TableColumn[] = [];
    for (const key of RETURN_EXCEPTION_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translate.instant(col.label),
        });
      }
    }
    if (this.hasRowActions()) {
      const actionsCol = fromConfig(RETURN_EXCEPTION_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translate.instant(actionsCol.label),
        });
      }
    }
    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      RETURN_EXCEPTION_TABLE_COLUMNS.find((c) => c.prop === prop);
    return RETURN_EXCEPTION_FLEXIBLE_COLUMNS_ORDER.map((prop) => {
      const col = fromConfig(prop);
      return col
        ? { ...col, label: this.translate.instant(col.label) }
        : null;
    }).filter(Boolean) as TableColumn[];
  });

  ngOnInit(): void {
    if (!this.canView()) {
      return;
    }
    this.loadData();
  }

  ngOnDestroy(): void {
    this.dialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const buttonElement =
      (target.closest('p-button')?.querySelector('button') as HTMLElement) ||
      (target.closest('button') as HTMLElement) ||
      target;
    this.advancedTable?.openColumnSelector(buttonElement);
  }

  onSearch(data: SearchFormData): void {
    this.searchParams.set({ ...data });
    this.first.set(0);
    this.loadData();
  }

  onReset(): void {
    this.searchParams.set({});
    this.first.set(0);
    this.loadData();
  }

  onPageChange(event: { first?: number; rows?: number; page?: number }): void {
    this.first.set(event.first ?? 0);
    if (event.rows) {
      this.pageSize.set(event.rows);
    }
    this.loadData();
  }

  onSelectionChange(rows: ReturnExceptionListItem[]): void {
    this.selectedItems.set(rows ?? []);
  }

  onView(record: ReturnExceptionListItem): void {
    if (!record.id) {
      return;
    }
    this.dialogRef = this.dialogService.open(
      ReturnExceptionDetailDialogComponent,
      {
        header: this.translate.instant('return_exception.detail.title', {
          ticketCode: record.ticketCode ?? '',
        }),
        width: '860px',
        modal: true,
        closable: true,
        data: { id: record.id },
      },
    );
  }

  onApprove(record: ReturnExceptionListItem): void {
    if (!this.canApprove() || !record.id || !this.isPending(record)) {
      return;
    }
    this.openConfirm(
      'return_exception.dialog.approve_title',
      'return_exception.messages.confirm_approve',
      { ticketCode: record.ticketCode ?? '' },
      () => this.executeApprove([record.id!]),
    );
  }

  onCancel(record: ReturnExceptionListItem): void {
    if (!this.canCancel() || !record.id || !this.isPending(record)) {
      return;
    }
    this.openCancelDialog([record.id]);
  }

  onBulkApprove(): void {
    const ids = this.pendingSelectedIds();
    if (!this.canApprove() || ids.length === 0) {
      return;
    }
    this.openConfirm(
      'return_exception.dialog.bulk_approve_title',
      'return_exception.messages.confirm_bulk_approve',
      { count: ids.length },
      () => this.executeApprove(ids),
    );
  }

  onBulkCancel(): void {
    const ids = this.pendingSelectedIds();
    if (!this.canCancel() || ids.length === 0) {
      return;
    }
    this.openCancelDialog(ids);
  }

  onConfirmationDialogAction(action: string): void {
    this.showConfirmationDialog.set(false);
    if (action === 'confirm' && this.confirmationHandler) {
      this.confirmationHandler();
    }
    this.confirmationHandler = null;
  }

  isPending(record: ReturnExceptionListItem): boolean {
    return record.status === RETURN_EXCEPTION_STATUS.PENDING_APPROVAL;
  }

  getStatusLabel(status?: string): string {
    switch (status) {
      case RETURN_EXCEPTION_STATUS.PENDING_APPROVAL:
        return this.translate.instant('return_exception.status.pending_approval');
      case RETURN_EXCEPTION_STATUS.APPROVED:
        return this.translate.instant('return_exception.status.approved');
      case RETURN_EXCEPTION_STATUS.INVALID:
        return this.translate.instant('return_exception.status.invalid');
      default:
        return status ?? '';
    }
  }

  getStatusSeverity(status?: string): string {
    switch (status) {
      case RETURN_EXCEPTION_STATUS.PENDING_APPROVAL:
        return 'warn';
      case RETURN_EXCEPTION_STATUS.APPROVED:
        return 'success';
      case RETURN_EXCEPTION_STATUS.INVALID:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  formatDateTime(value?: string): string {
    return value ? formatDateTime(value) : '';
  }

  private pendingSelectedIds(): number[] {
    return this.selectedItems()
      .filter((row) => this.isPending(row) && row.id != null)
      .map((row) => row.id!);
  }

  private openConfirm(
    titleKey: string,
    messageKey: string,
    params: Record<string, unknown>,
    handler: () => void,
  ): void {
    this.confirmationTitle.set(this.translate.instant(titleKey));
    this.confirmationMessage.set(this.translate.instant(messageKey, params));
    this.confirmationButtons.set([
      {
        label: 'common.actions.cancel',
        action: 'cancel',
        outlined: true,
      },
      {
        label: 'common.actions.confirm',
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.confirmationHandler = handler;
    this.showConfirmationDialog.set(true);
  }

  private openCancelDialog(ids: number[]): void {
    this.dialogRef = this.dialogService.open(
      ReturnExceptionCancelDialogComponent,
      {
        header: this.translate.instant('return_exception.cancel_dialog.title'),
        width: '480px',
        modal: true,
        closable: true,
      },
    );
    this.dialogRef.onClose
      .pipe(takeUntil(this.destroy$))
      .subscribe((cancelReason: string | null) => {
        if (!cancelReason?.trim()) {
          return;
        }
        this.executeCancel(ids, cancelReason.trim());
      });
  }

  private executeApprove(ids: number[]): void {
    this.actionLoading.set(true);
    const handlers = {
      next: () => {
        this.actionLoading.set(false);
        this.selectedItems.set([]);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success'),
          detail: this.translate.instant(
            'return_exception.messages.approve_success',
          ),
        });
        this.loadData();
      },
      error: (error: unknown) => {
        this.actionLoading.set(false);
        this.errorDialogService.showError(
          this.errorTranslatorService.translate(
            extractErrorMessage(error),
            'return_exception.messages.approve_error',
          ),
        );
      },
    };

    if (ids.length === 1) {
      this.featureService
        .approve(ids[0])
        .pipe(takeUntil(this.destroy$))
        .subscribe(handlers);
      return;
    }

    this.featureService
      .approveBatch(ids)
      .pipe(takeUntil(this.destroy$))
      .subscribe(handlers);
  }

  private executeCancel(ids: number[], cancelReason: string): void {
    this.actionLoading.set(true);
    const handlers = {
      next: () => {
        this.actionLoading.set(false);
        this.selectedItems.set([]);
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.messages.success'),
          detail: this.translate.instant(
            'return_exception.messages.cancel_success',
          ),
        });
        this.loadData();
      },
      error: (error: unknown) => {
        this.actionLoading.set(false);
        this.errorDialogService.showError(
          this.errorTranslatorService.translate(
            extractErrorMessage(error),
            'return_exception.messages.cancel_error',
          ),
        );
      },
    };

    if (ids.length === 1) {
      this.featureService
        .cancel(ids[0], cancelReason)
        .pipe(takeUntil(this.destroy$))
        .subscribe(handlers);
      return;
    }

    this.featureService
      .cancelBatch(ids, cancelReason)
      .pipe(takeUntil(this.destroy$))
      .subscribe(handlers);
  }

  private loadData(): void {
    if (!this.canView()) {
      return;
    }
    this.loading.set(true);
    const page = Math.floor(this.first() / this.pageSize());
    const params = this.searchParams();
    this.featureService
      .page({
        page,
        size: this.pageSize(),
        sortBy: RETURN_EXCEPTION_DEFAULT_CONFIG.DEFAULT_SORT_FIELD,
        ascending: RETURN_EXCEPTION_DEFAULT_CONFIG.DEFAULT_SORT_ASCENDING,
        ticketCode: params['ticketCode'] as string | undefined,
        storeCode: params['storeCode'] as string | undefined,
        customerPhone: params['customerPhone'] as string | undefined,
        status: params['status'] as string | undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.items.set(result.items);
          this.totalRecords.set(result.totalRecords);
          this.loading.set(false);
        },
        error: (error: unknown) => {
          this.loading.set(false);
          this.errorDialogService.showError(
            this.errorTranslatorService.translate(
              extractErrorMessage(error),
              'return_exception.messages.load_error',
            ),
          );
        },
      });
  }
}
